import { Ref } from "react";
import CreatorFragmentConfig from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragmentConfig";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import CustomTaskDescriptionCreatorFragment from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CustomTaskDescriptionCreatorFragment/CustomTaskDescriptionCreatorFragment";
import SwitchCreatorFragment from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/SwitchCreatorFragment/SwitchCreatorFragment";
import AddImagesCreatorFragment from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/AddImagesCreatorFragment/AddImagesCreatorFragment";
import TextModalCreatorFragment from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/TextModalCreatorFragment/TextModalCreatorFragment";
import AssignToCreatorFragment from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/AssignToCreatorFragment/AssignToCreatorFragment";
import InvalidCreatorFragment from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/InvalidCreatorFragment/InvalidCreatorFragment";
import { Loading } from "@coworker/apprestructured/src/task-management/loading";
import AttachFilesCreatorFragment from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/AttachFilesCreatorFragment/AttachFilesCreatorFragment";
import AttachArticlesCreatorFragment from "./AttachArticlesCreatorFragment/AttachArticlesCreatorFragment";
import AddLocationCreatorFragment from "./AddLocationCreatorFragment/AddLocationCreatorFragment";
import SelectItemWithDescriptionCreatorFragment from "./SelectItemWithDescriptionCreatorFragment/SelectItemWithDescriptionCreatorFragment";
import AttachArticleCreatorFragment from "./AttachArticleCreatorFragment/AttachArticleCreatorFragment";
import { TaskType } from "../../../types/TaskType";

export type CreatorFragmentHandle = {
  valid: boolean;
};

export type CreatorFragmentCommonProps = {
  // Block all user interaction with this fragment, usually while making api requests.
  blockInput: boolean;
  // The task being created or edited.
  task: Record<string, unknown> | Loading;
  // Callback for making changes to the task that is being created or edited.
  // Note that this doesn't trigger any api request. It just adds a part to the PatchTask
  // that is sent when the user explicitly creates or saves the task later on.
  onTaskChange: (part: Record<string, unknown>) => void;
  // Whether to show error validation at the top level.
  // This is toggled if the user tries to create or save a task containing invalid data.
  showValidation: boolean;
  taskType: TaskType | Loading;
};

type CreatorFragmentProps = {
  config: CreatorFragmentConfig;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function CreatorFragment({ config, commonProps, ref }: CreatorFragmentProps) {
  const { id, props: configProps } = config;
  switch (id) {
    case "custom-task-description-creator-fragment": {
      return (
        <CustomTaskDescriptionCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "add-images-creator-fragment": {
      return (
        <AddImagesCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "text-modal-creator-fragment": {
      return (
        <TextModalCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "switch-creator-fragment": {
      return (
        <SwitchCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "assign-to-creator-fragment": {
      return (
        <AssignToCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "attach-files-creator-fragment": {
      return (
        <AttachFilesCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "attach-articles-creator-fragment": {
      return (
        <AttachArticlesCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "add-location-creator-fragment": {
      return (
        <AddLocationCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "select-item-with-description-creator-fragment": {
      return (
        <SelectItemWithDescriptionCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "attach-article-creator-fragment": {
      return (
        <AttachArticleCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    default: {
      console.error(`'${id}' does not map to any creator fragment component`);
      reportMessageToSentry(
        `Creator fragment config id does not map to any creator fragment component`,
        {
          id,
        },
        "error"
      );
      return <InvalidCreatorFragment />;
    }
  }
}

export default CreatorFragment;
